import { useState } from 'react'
import tw, { css } from 'twin.macro'
import { useCleanPostData } from '../contexts/CleanPostDataContext'
import Button, { ButtonSize, ButtonVariant } from '@components/DesignSystem/Button'
import { useTranslation } from '@i18n'
import { PlusIcon, CheckIcon } from '@components/Icons'
import { useDispatch, useSelector } from 'react-redux'
import { postUserFollow } from '@app-core/post/actions'
import { dispatchCustomEvent, NavigationEvents, FollowUIEvents } from '@libs/customEvents'
import { FollowActionType } from '@libs/eventTracking/article'
import { TrackingSection } from '../contexts/TrackingSectionContext'
import LoginAlert from '@components/Login/LoginAlert'
import { checkIsGuest } from '@libs/auth-utils'
import PopLink from '@components/PopLink'
import { publicUrl } from '@libs/appUrl'
import { urlToLink } from '@libs/postTools'

const nameAndDescriptionStyle = css`
  word-break: break-all;
`

const DescriptionStyle = css`
  a {
    ${tw`text-primary-indigo`}
  }
`

function ArticleAuthorInfo(props) {
  const { isHideArticleSomeMessage } = props
  const data = useCleanPostData()
  const { isFollowingAuthor, userProfile, post } = data
  const isSecret = post.clas.includes('secret')

  const userDes = post.userInfo.des

  const handleOnClickUserProfile = () => {
    dispatchCustomEvent(NavigationEvents.ENTER_USER_PROFILE, {
      section: TrackingSection.ARTICLE_AUTHOR_INFO,
    })
  }

  return (
    <div className="article-user-info" tw="flex flex-row items-center justify-between">
      <div tw="flex flex-row items-center space-x-3 w-full">
        <PopLink
          url={publicUrl(userProfile.link)}
          tw="h-14 w-14 rounded-full overflow-hidden hover:opacity-60"
          onClick={handleOnClickUserProfile}
          disableOnClick={isSecret}
        >
          <img src={userProfile.avatarUrl} alt={userProfile.name} />
        </PopLink>
        <div
          css={[
            tw`flex`,
            tw`flex-col`,
            tw`space-y-1`,
            isHideArticleSomeMessage ? tw`w-4/5` : tw`w-3/5`,
            nameAndDescriptionStyle,
          ]}
        >
          <PopLink
            url={publicUrl(userProfile.link)}
            css={[tw`text-black`, tw`text-b1`, tw`leading-h4`, tw`font-medium`]}
            onClick={handleOnClickUserProfile}
            disableOnClick={isSecret}
          >
            {userProfile.name}
          </PopLink>

          <PopLink
            url={publicUrl(userProfile.link)}
            tw="text-gray-600 text-b2"
            onClick={handleOnClickUserProfile}
            disableOnClick={isSecret}
          >
            <div
              css={[DescriptionStyle]}
              dangerouslySetInnerHTML={{
                __html: userDes ? urlToLink(userDes.replace(/\r|\n/g, '<br />')) : '',
              }}
            />
          </PopLink>
        </div>
      </div>
      {!isHideArticleSomeMessage && (isFollowingAuthor ? <FollowingButton /> : <FollowButton />)}
    </div>
  )
}

export default ArticleAuthorInfo

export function FollowButton() {
  const { t } = useTranslation('post')
  const {
    post: { userInfo },
  } = useCleanPostData()
  const dispatch = useDispatch()
  const { openLoginAlert, loginAlert } = useLoginAlert()
  const isGuest = useCheckGuest()

  const follow = () => {
    dispatchCustomEvent(FollowUIEvents.CLICK_FOLLOW, {
      actionType: FollowActionType.FOLLOW,
      section: TrackingSection.ARTICLE_AUTHOR_INFO,
    })

    if (isGuest) return openLoginAlert()

    dispatch(postUserFollow({ followID: userInfo.mid, follow: 1 }))
  }

  return (
    <>
      <Button onClick={follow} size={ButtonSize.medium} variant={ButtonVariant.primary}>
        <PlusIcon />
        <span>{t('follow')}</span>
      </Button>
      {loginAlert}
    </>
  )
}

export function FollowingButton() {
  const { t } = useTranslation('post')
  const {
    post: { userInfo },
  } = useCleanPostData()
  const dispatch = useDispatch()

  const unfollow = () => {
    dispatchCustomEvent(FollowUIEvents.CLICK_FOLLOW, {
      actionType: FollowActionType.UNFOLLOW,
      section: TrackingSection.ARTICLE_AUTHOR_INFO,
    })
    dispatch(postUserFollow({ followID: userInfo.mid, follow: 0 }))
  }

  return (
    <Button onClick={unfollow} size={ButtonSize.medium} variant={ButtonVariant.outlinePrimary}>
      <CheckIcon />
      <span>{t('following')}</span>
    </Button>
  )
}

const useLoginAlert = () => {
  const [isOpen, setIsOpen] = useState(false)

  const loginAlert = <LoginAlert target="_self" isOpen={isOpen} onClose={() => setIsOpen(false)} />
  const openLoginAlert = () => setIsOpen(true)

  return { loginAlert, openLoginAlert }
}

const useCheckGuest = (): boolean => {
  return useSelector<any, boolean>((state) => {
    return checkIsGuest(state.currentUser.getIn(['userInfo', 'type']))
  })
}
